import {
  NetworkStatus,
  QueryHookOptions,
  QueryResultWrapper,
  useLazyQuery,
  useQuery,
  useSubscription,
} from '@apollo/client'
import gql from 'graphql-tag'
import {orderBy, unionBy} from 'lodash-es'
import {useCallback, useMemo, useRef, useState} from 'react'
import {usePageVisibility} from 'react-page-visibility'
import {useParams} from 'react-router'

import {Chats, QueryRoot} from '../../types/graphqlSchema'
import useSessionMerchantUser from '../useSessionMerchantUser'

// TODO: Generalize into a useSubscribedQuery() hook once sorting + favicon are moved out
/* This hook firstly runs a graphql query and then subscribes for more results
 * Advantage over using a subscription only: queries are replied immediately while the
 * first subscription response can take a while to come in. So this hook can be used
 * where time to first data is crucial for UX.
 */
const useChatsQueryAndSubscription = (
  chatsBoolExpQueryFilters: Record<string, any>,
  chatFields: string,
  chatFilter: ChatFilter,
  options?: QueryHookOptions,
) => {
  const channelId = useParams().channelId?.replaceAll('-', '')
  const {merchantUser: {id: sessionMerchantUserId} = {}} = useSessionMerchantUser()
  const isPageVisible = usePageVisibility() ?? true
  // Cursor should not be reinitialized on re-render, otherwise the subscription will be
  // constantly updated, and we will never receive any new data via the subscription.
  const cursor = useRef(new Date().toISOString())
  const [hasEarlierChats, setHasEarlierChats] = useState(true)
  // Offset value before initially loading any chats
  const chatOffsetRef = useRef<number>(CHATS_LOAD_MORE_OFFSET)
  // The query name used here (i.e. `chats_channelId_mine`) is necessary to
  // selectively update queries when a chat is updated on the webapp e.g.
  // when a chat is archived/unassigned.
  const queryDocument = useMemo(() => gql`
    query chats_${channelId}_${chatFilter}($offset: Int, $where: chats_bool_exp!){
      chats(
        limit: ${CHATS_LOAD_MORE_OFFSET}
        offset: $offset
        order_by: {lastActivityTimestamp: desc}
        where: $where
      ){${chatFields}}
    }
  `, [channelId, chatFields, chatFilter])
  const extendedQueryOptions = useMemo<QueryHookOptions>(() => ({
    ...options,
    variables: {
      ...options?.variables,
      where: {...options?.variables?.where, ...chatsBoolExpQueryFilters},
    },
  }), [chatsBoolExpQueryFilters, options])
  const {
    data: {chats} = {},
    error,
    fetchMore,
    loading,
    networkStatus,
  } = useQuery<QueryRoot['chats']>(queryDocument, extendedQueryOptions)
  // We only subscribe to the chatId in our subscription to (partly) reduce complexity of
  // the query. Thus, a lazy query is needed to fetch the complete chat fields whenever
  // the subscription fires with new chats.
  const [fetchIncomingChats] = useLazyQuery<QueryRoot['chats']>(
    gql`query($chatIds: [uuid!]!){
      chats(where: {id: {_in: $chatIds}}){${chatFields}}
    }`,
    {fetchPolicy: 'network-only'}
  )
  useSubscription<QueryRoot['chats_event_logs']>(
    CHATS_EVENT_LOGS_STREAM_SUBSCRIPTION,
    {
      async onData({
        client: {cache},
        data: {data: {chats_event_logs_stream = []} = {}},
      }) {
        if (!chats_event_logs_stream.length) return
        const chatIds = chats_event_logs_stream.map(c => c.chatId)
        const {
          data: {chats: incomingChats = []} = {},
        } = await fetchIncomingChats({variables: {chatIds}})
        const filters = ['mine', 'unassigned', 'all', 'archived']
        filters.forEach(filter => {
          const cacheOptions = {
            query: queryDocument,
            variables: {
              where: {
                isArchived: {_eq: false},
                ...options?.variables?.where,
                ...({
                  all: {},
                  archived: {isArchived: {_eq: true}},
                  mine: {assignedMerchantUserId: {_eq: sessionMerchantUserId}},
                  unassigned: {assignedMerchantUserId: {_is_null: true}},
                }[filter]),
              },
            },
          }
          const prev = cache.readQuery<QueryRoot>(cacheOptions)
          if (!prev) return
          cache.writeQuery({
            ...cacheOptions,
            data: {
              chats: orderBy(
                unionBy(
                  incomingChats as Chats[], prev?.chats, 'id'
                ).filter(
                  ({assignedMerchantUser, isArchived}) => ({
                    all: !isArchived,
                    archived: isArchived,
                    mine: (
                      assignedMerchantUser?.id === sessionMerchantUserId
                    ) && !isArchived,
                    unassigned: !assignedMerchantUser && !isArchived,
                  }[filter])
                ),
                'lastActivityTimestamp',
                'desc',
              ),
            },
          })
        })
      },
      skip: (extendedQueryOptions.skip || !isPageVisible || !sessionMerchantUserId),
      variables: {
        cursor: cursor.current,
        limit: 10,
        where: {chat: options?.variables?.where},
      },
    }
  )
  const fetchEarlierChats = useCallback(async () => {
    try {
      await fetchMore({
        updateQuery: (
          {chats: previousChats = []} = {} as any,
          {fetchMoreResult: {chats: moreChats} = {}},
        ) => {
          if (moreChats?.length) chatOffsetRef.current += CHATS_LOAD_MORE_OFFSET
          moreChats && setHasEarlierChats(!!moreChats.length)
          return {
            chats: unionBy(previousChats, moreChats, 'id'),
          } as QueryResultWrapper<QueryRoot['chats']>
        },
        variables: {offset: chatOffsetRef.current},
      })
    }
    catch (error) {
      // Invariant violation: 17 is thrown when component is unmounted while query is
      // still in progress, e.g. when user navigates to different channel/page.
      // See: https://github.com/apollographql/apollo-client/issues/4114#issuecomment-502111099
      if ((error as Error).toString().includes('Invariant Violation')) {
        return
      }
      throw error
    }
  }, [fetchMore])
  const onResetFetchEarlierChatsOffset = useCallback(() => {
    chatOffsetRef.current = CHATS_LOAD_MORE_OFFSET
    setHasEarlierChats(true)
  }, [setHasEarlierChats])
  return {
    chats,
    error,
    fetchEarlierChats,
    hasEarlierChats,
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
    loading,
    onResetFetchEarlierChatsOffset,
  }
}

const CHATS_EVENT_LOGS_STREAM_SUBSCRIPTION = gql`
  subscription($cursor: timestamptz!, $limit: Int!, $where: chats_event_logs_bool_exp!){
    chats_event_logs_stream(
      batch_size: $limit
      cursor: {initial_value: {insertionTimestamp: $cursor}, ordering: ASC}
      where: $where
    ){chatId id}
  }
`

export const CHATS_LOAD_MORE_OFFSET = 20

export default useChatsQueryAndSubscription
